import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent } from '@/components/ui/card';
import { fetchParishBySlug } from '@/services/api';
import { MapPin, Church, Users, ArrowLeft, ShieldCheck } from 'lucide-react';
import ImageHelper from '../lib/imageHelper';

interface Parish {
  id: number;
  name: string;
  slug: string;
  location?: string;
  photo?: string;
  archdeaconry?: {
    id: number;
    name: string;
    slug: string;
  };
  priests?: {
    id: number;
    title: string;
    name: string;
    slug: string;
    photo?: string;
  }[];
}

const ParishProfile = () => {
  const { slug } = useParams<{ slug: string }>();
  const { toast } = useToast();
  const [parish, setParish] = useState<Parish | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadParish = async () => {
      try {
        const response = await fetchParishBySlug(slug!);
        setParish(response.data);
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to fetch parish details.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };
    loadParish();
  }, [slug, toast]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#fcfaf7]">
      <div className="animate-pulse flex flex-col items-center">
        <Church className="h-12 w-12 text-amber-200 mb-4" />
        <p className="font-serif italic text-slate-500">Opening Parish Records...</p>
      </div>
    </div>
  );

  if (!parish) return (
    <div className="min-h-screen flex items-center justify-center bg-[#fcfaf7]">
      <div className="text-center">
        <h2 className="text-2xl font-serif text-slate-800">Parish Not Found</h2>
        <Link to="/parishes" className="text-amber-700 hover:underline mt-4 inline-block">Return to Directory</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#fcfaf7] pb-20">
      {/* Top Navigation */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Link to="/parishes" className="flex items-center text-slate-500 hover:text-amber-700 transition-colors group">
          <ArrowLeft className="h-4 w-4 mr-2 group-hover:-translate-x-1 transition-transform" />
          Back to Parishes
        </Link>
      </div>

      <div className="max-w-6xl mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">

          {/* Left Column: Parish Identity */}
          <div className="lg:col-span-2 space-y-8">
            <div className="relative aspect-video rounded-3xl overflow-hidden shadow-2xl ring-1 ring-slate-200 bg-white">
              <ImageHelper
                src={parish.photo}
                alt={parish.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent" />
              <div className="absolute bottom-8 left-8 text-white">
                <span className="bg-amber-600 text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1 rounded-full mb-3 inline-block">
                  Established Parish
                </span>
                <h1 className="text-4xl md:text-5xl font-serif font-bold leading-tight">
                  {parish.name}
                </h1>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-sm ring-1 ring-slate-100">
              <h2 className="text-xl font-serif font-bold text-slate-900 mb-6 flex items-center">
                <ShieldCheck className="h-5 w-5 mr-2 text-amber-600" />
                Administrative Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="flex items-start gap-4">
                  <div className="bg-amber-50 p-3 rounded-2xl">
                    <MapPin className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Location</p>
                    <p className="text-slate-700 font-medium">{parish.location || 'Ibadan, Nigeria'}</p>
                  </div>
                </div>

                {parish.archdeaconry && (
                  <div className="flex items-start gap-4">
                    <div className="bg-slate-50 p-3 rounded-2xl">
                      <Church className="h-6 w-6 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Archdeaconry</p>
                      <Link
                        to={`/archdeaconries/${parish.archdeaconry.slug}`}
                        className="text-amber-700 font-bold hover:text-slate-900 transition-colors"
                      >
                        {parish.archdeaconry.name}
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column: Resident Clergy */}
          <div className="space-y-6">
            <div className="bg-white rounded-3xl p-8 shadow-sm ring-1 ring-slate-100">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-xl font-serif font-bold text-slate-900 flex items-center">
                  <Users className="h-5 w-5 mr-2 text-amber-600" />
                  Resident Clergy
                </h2>
                <span className="text-xs bg-slate-100 text-slate-500 font-bold px-2 py-1 rounded-md">
                  {parish.priests?.length || 0}
                </span>
              </div>

              {parish.priests && parish.priests.length > 0 ? (
                <div className="space-y-6">
                  {parish.priests.map((clergy) => (
                    <Link
                      key={clergy.id}
                      to={`/clergy/${clergy.slug}`}
                      className="flex items-center gap-4 group hover:bg-slate-50 p-2 -m-2 rounded-2xl transition-all"
                    >
                      <div className="w-16 h-16 rounded-2xl overflow-hidden ring-2 ring-slate-100 group-hover:ring-amber-200 transition-all">
                        <img
                          src={clergy.photo || "https://res.cloudinary.com/dwrqyrm84/image/upload/t_dp_logo/v1749389564/mq1jwwqt9xxehogbwlem.png"}
                          alt={clergy.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-amber-600 uppercase tracking-tighter mb-0.5">
                          {clergy.title}
                        </p>
                        <p className="text-slate-900 font-bold group-hover:text-amber-800 transition-colors">
                          {clergy.name}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-10 border-2 border-dashed border-slate-50 rounded-2xl">
                  <p className="text-sm text-slate-400 italic">No clergy assignments found.</p>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ParishProfile;